import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button, message, Typography, Grid, Space, Card, Layout, Flex } from 'antd';
import { UserAddOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, getUserToken, getProducts, deleteUserProfilePicture } from './UsersServices';
import UserTable from './UserTable/UserTable';
import UserModal from './UserModal/UserModal';
import UserViewModal from './UserViewModal/UserViewModal';
import { format } from 'date-fns';
import { Input } from 'antd';
import { debounce } from 'lodash';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { saveAuthDataForAdmin } from '../../Config/ApiConfig';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { userFilterFields } from '../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../Shared/Filters/useFilterOptionFields';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useVisualHighlight from '../../Hooks/useVisualHighlight';
import { Resource, Action } from '../../Constants/socket';

dayjs.extend(utc);

const { Title } = Typography;
const { useBreakpoint } = Grid;

const USER_FILTER_OPTION_RESOURCES = {
    disabled_by_id: FILTER_OPTION_RESOURCE.USER,
    discount_added_by_id: FILTER_OPTION_RESOURCE.USER
};

const UserList = () => {
    const { changeUser } = useGlobal();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const { handleError } = useErrorHandler();
    const { socket } = useSocket();
    const [modalVisible, setModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [viewingUser, setViewingUser] = useState(null);
    const [searchString, setSearchString] = useState('');
    const searchStringRef = React.useRef('');
    const fetchAbortControllerRef = React.useRef(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort_by, setSortBy] = useState(undefined);
    const [sort_direction, setSortDirection] = useState(undefined);
    const [chipFilters, setChipFilters] = useState([]);
    const [normalizedChipFilters, setNormalizedChipFilters] = useState({});
    const [pagination, setPagination] = useState({
        total: 0,
        last: false,
    });

    const screens = useBreakpoint();

    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Subscribe to the generic USER room for CREATE events
    useSocketSubscription(Resource.USER, null);

    // Subscribe to per-user IDs for UPDATE/DELETE events
    const userIds = useMemo(() => users.map(u => u.id).filter(Boolean), [users]);
    useSocketSubscription(Resource.USER, userIds.length > 0 ? userIds : null);
    const filterFields = useFilterOptionFields(userFilterFields, USER_FILTER_OPTION_RESOURCES, { handleError });

    const fetchUsers = useCallback(async (search_string) => {
        // Cancel any in-flight request before starting a new one
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
        }
        const controller = new AbortController();
        fetchAbortControllerRef.current = controller;

        try {
            setLoading(true);
            const response = await getUsers({
                page,
                size: pageSize,
                sort_by,
                sort_direction,
                search_string: search_string !== undefined ? search_string : searchStringRef.current,
                filters: normalizedChipFilters || {},
                signal: controller.signal,
            });
            setUsers(
                response.data.map(user => ({
                    ...user,
                    discount_expiry: user.discount_expiry ? dayjs(user.discount_expiry) : null
                }))
            );
            setPagination({
                total: response.metadata.total,
                last: response.metadata.last,
            });
        } catch (error) {
            // Silently ignore cancelled requests
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
            handleError(error);
        } finally {
            // Only clear loading if this is still the active request
            if (fetchAbortControllerRef.current === controller) {
                setLoading(false);
            }
        }
    }, [setLoading, handleError, page, pageSize, sort_by, sort_direction, normalizedChipFilters]);

    // Cleanup abort controller on unmount
    useEffect(() => {
        return () => {
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
            }
        };
    }, []);

    // Handle new user creation — refetch the current page
    const handleUserCreated = useCallback((data) => {
        fetchUsers();

        // Subscribe to the new user from server_datetime to cover the readiness gap
        if (data?.id && data?.server_datetime && socket) {
            socket.subscribeToResource(Resource.USER, [data.id], data.server_datetime);
        }
        if (data?.id) highlightRow(data.id);
    }, [fetchUsers, socket, highlightRow]);

    // Handle user updates — merge inline
    const handleUserUpdated = useCallback((data) => {
        setUsers(prev => prev.map(u => u.id === data.id ? { ...u, ...data } : u));
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [highlightRow]);

    // Handle user deletion — refetch the current page
    const handleUserDeleted = useCallback((data) => {
        setUsers(prev => prev.filter(u => u.id !== data.id));
    }, []);

    useSocketEvent(Resource.USER, Action.CREATE, handleUserCreated);
    useSocketEvent(Resource.USER, Action.UPDATE, handleUserUpdated);
    useSocketEvent(Resource.USER, Action.DELETE, handleUserDeleted);


    const getUserTokenCall = useCallback(async (id) => {
        setLoading(true);
        try {
            const { data } = await getUserToken(id);
            saveAuthDataForAdmin(data);
            changeUser(data.user);
            if (!data.user?.is_admin) {
                navigate('/');
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, changeUser, navigate]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchString(value);
        searchStringRef.current = value;
        debouncedFetchUsers(value);
    };

    const debouncedFetchUsers = useCallback(
        debounce((search) => {
            setPage(1);
            fetchUsers(search);
        }, 500),
        [fetchUsers]
    );

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedFetchUsers?.cancel?.();
        };
    }, [debouncedFetchUsers]);

    // Fetch on page/sort/filter changes (NOT on search — that's handled by debounce)
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleTableChange = (pagination, _tableFilters, sorter) => {
        const { order, columnKey, field } = sorter || {};
        // Use columnKey (from column.key) with fallback to field (from column.dataIndex)
        let sort_by = columnKey || field;
        if (sort_by === 'role') {
            sort_by = 'is_admin';
        }
        const sort_direction = order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined;
        if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
        }

        // Reset sort state when order is undefined (sorting is removed)
        if (order === undefined) {
            setSortBy(undefined);
            setSortDirection(undefined);
        } else if (sort_by) {
            setSortBy(sort_by);

            if (sort_direction) {
                setSortDirection(sort_direction);
            }
        }
        if (pagination.current) {
            setPage(pagination.current);
        }
    };

    const handleChipSortChange = useCallback(({ sort_by: nextSortBy, sort_direction: nextSortDirection }) => {
        setSortBy(nextSortBy);
        setSortDirection(nextSortDirection);
        setPage(1);
    }, []);

    const showModal = (user = null) => {
        setEditingUser(user);
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        setEditingUser(null);
    };

    const showViewModal = (user = null) => {
        setViewingUser(user);
        setViewModalVisible(true);
    };

    const handleViewCancel = () => {
        setViewModalVisible(false);
        setViewingUser(null);
    };

    const handleFormSubmit = async (values) => {
        try {
            const defaultValues = {
                ...values,
                discount_percentage: values.discount_percentage
                    ? parseFloat(values.discount_percentage)
                    : undefined,
                discount_expiry: values.discount_expiry
                    ? dayjs(values.discount_expiry).utc().format('YYYY-MM-DDTHH:mm:ss+00:00')
                    : undefined,
            }
            let updatedValues = {
                ...defaultValues,
            };
            if (defaultValues.timezone) {
                const timezone = defaultValues.timezone;
                const offset = moment.tz(timezone).utcOffset() / 60;
                updatedValues = {
                    ...defaultValues,
                    timezone: offset,
                    timezone_name: timezone,
                };
            }
            const filteredValues = Object.fromEntries(
                Object.entries(updatedValues).filter(([_, v]) => v != null)
            );
            if (editingUser) {
                const payload = await updateUser(editingUser.id, filteredValues);
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            } else {
                const payload = await createUser(filteredValues);
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
            handleCancel();
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const payload = await deleteUser(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProfilePicture = async (id) => {
        setLoading(true);
        try {
            const payload = await deleteUserProfilePicture(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Layout>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card
                    style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
                    size='medium'
                    title={t('user_management')}
                    extra={
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="medium"
                            onClick={() => showModal()}
                        >
                            {!screens.xs && t('add_user')}
                        </Button>
                    }
                    styles={{
                        body: {
                            padding: 16
                        }
                    }}
                >
                    <Flex
                        gap="middle"
                        wrap="wrap"
                        align="center"
                        justify="space-between"
                        style={{ width: '100%' }}
                    >
                        <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                            <Input
                                prefix={<SearchOutlined />}
                                placeholder={t('search_users')}
                                value={searchString}
                                onChange={handleSearch}
                                onFocus={() => setIsInputFocused(true)}
                                onBlur={() => setIsInputFocused(false)}
                                style={{
                                    width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                                    transition: 'width 0.4s ease-in-out'
                                }}
                            />
                        </Flex>

                        <SharedFilterBar
                            fields={filterFields}
                            value={chipFilters}
                            onChange={(nextFilters, normalized) => {
                                setChipFilters(nextFilters);
                                setNormalizedChipFilters(normalized);
                                setPage(1);
                                // fetchUsers will be triggered by useEffect via updated dependencies
                            }}
                            sortFields={[
                                { value: 'full_name', labelKey: 'full_name' },
                                { value: 'email', labelKey: 'email' },
                                { value: 'is_admin', labelKey: 'role' },
                                { value: 'gender', labelKey: 'gender' },
                                { value: 'is_verified', labelKey: 'verified' },
                                { value: 'is_disabled', labelKey: 'filters_field_disabled' },
                                { value: 'created_at', labelKey: 'created_at' },
                                { value: 'updated_at', labelKey: 'updated_at' },
                            ]}
                            sortValue={{ sort_by, sort_direction }}
                            onSortChange={handleChipSortChange}
                        />
                    </Flex>
                </Card>
                <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                    <UserTable
                        users={users}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            current: page,
                            pageSize: pageSize,
                        }}
                        screens={screens}
                        handleTableChange={handleTableChange}
                        showModal={showModal}
                        handleDelete={handleDelete}
                        showViewModal={showViewModal}
                        getUserTokenCall={getUserTokenCall}
                        setLoading={setLoading}
                        searchString={searchString}
                        sortBy={sort_by}
                        sortDirection={sort_direction}
                        getHighlightClass={getHighlightClass}
                    />
                </Card>
                <UserModal
                    modalVisible={modalVisible}
                    handleCancel={handleCancel}
                    handleSubmit={handleFormSubmit}
                    editingUser={editingUser}
                    screens={screens}
                    setLoading={setLoading}
                />
                <UserViewModal
                    modalVisible={viewModalVisible}
                    handleCancel={handleViewCancel}
                    user={viewingUser}
                    screens={screens}
                    onDeleteProfilePicture={handleDeleteProfilePicture}
                />
            </Space>
        </Layout>
    );
};

export default UserList;
